import is from '@sindresorhus/is';

import type {
    ActivityTimelineBlock,
    ActivityTimelineStep,
    ChatBlock,
    StatusUpdateBlock,
} from '../../data-contract/chat';

export type FlattenedBlock = { readonly block: ChatBlock; readonly eventId: string; readonly timestamp?: string };

export const mergeActivityTimelineSteps = (
    previous: ActivityTimelineBlock,
    next: ActivityTimelineBlock,
): ActivityTimelineBlock => {
    const stepsById = new Map(previous.steps.map((step) => [step.id, step]));
    for (const step of next.steps) {
        stepsById.set(step.id, step);
    }

    return {
        ...next,
        steps: [...stepsById.values()],
        usage: next.usage ?? previous.usage,
    };
};

const getStatusUpdateBaseKey = (block: StatusUpdateBlock): string => `${block.blockId}:${block.label}`;

const getOccurrenceKey = (baseKey: string, occurrence: number): string =>
    occurrence === 0 ? baseKey : `${baseKey}#${occurrence}`;

const normalizeStatusTimelineSteps = (
    stepsByKey: ReadonlyMap<string, ActivityTimelineStep>,
): ActivityTimelineStep[] => {
    const steps = [...stepsByKey.values()];
    const lastActiveIndex = steps.findLastIndex((step) => step.status === 'active');
    if (lastActiveIndex < 1) return steps;
    return steps.map((step, index) =>
        step.status === 'active' && index < lastActiveIndex ? { ...step, status: 'complete' } : step,
    );
};

const completeAllSteps = (stepsByKey: ReadonlyMap<string, ActivityTimelineStep>): ActivityTimelineStep[] =>
    [...stepsByKey.values()].map((step) => (step.status === 'active' ? { ...step, status: 'complete' } : step));

const parseTimestamp = (timestamp: string | undefined): number | null => {
    if (is.undefined(timestamp)) return null;
    const parsed = Date.parse(timestamp);
    return Number.isNaN(parsed) ? null : parsed;
};

type StatusTimelineDraft = {
    readonly blockId: string;
    readonly eventId: string;
    firstTimestamp: number | null;
    readonly index: number;
    latestTimestamp: number | null;
    readonly occurrenceByBaseKey: Map<string, number>;
    readonly stepsByKey: Map<string, ActivityTimelineStep>;
};

const createStatusTimelineDraft = (index: number, item: FlattenedBlock): StatusTimelineDraft => ({
    blockId: item.block.blockId,
    eventId: item.eventId,
    firstTimestamp: null,
    index,
    latestTimestamp: null,
    occurrenceByBaseKey: new Map(),
    stepsByKey: new Map(),
});

const updateStatusTimelineTimestamp = (draft: StatusTimelineDraft, timestamp: number): void => {
    draft.firstTimestamp = is.null(draft.firstTimestamp) ? timestamp : Math.min(draft.firstTimestamp, timestamp);
    draft.latestTimestamp = is.null(draft.latestTimestamp) ? timestamp : Math.max(draft.latestTimestamp, timestamp);
};

// Each run of a step gets its own row: a step that starts again after it completed is appended as a
// new occurrence instead of flipping the finished row back to active.
const addStatusTimelineStep = (draft: StatusTimelineDraft, item: FlattenedBlock): void => {
    if (item.block.type !== 'status-update') return;

    const baseKey = getStatusUpdateBaseKey(item.block);
    const currentOccurrence = draft.occurrenceByBaseKey.get(baseKey) ?? 0;
    const currentStep = draft.stepsByKey.get(getOccurrenceKey(baseKey, currentOccurrence));
    const startsNewOccurrence = currentStep?.status === 'complete' && !item.block.finished;
    const occurrence = startsNewOccurrence ? currentOccurrence + 1 : currentOccurrence;
    const key = getOccurrenceKey(baseKey, occurrence);

    draft.occurrenceByBaseKey.set(baseKey, occurrence);
    draft.stepsByKey.set(key, {
        id: key,
        label: item.block.label,
        status: item.block.finished ? 'complete' : 'active',
    });

    const timestamp = parseTimestamp(item.timestamp);
    if (!is.null(timestamp)) {
        updateStatusTimelineTimestamp(draft, timestamp);
    }
};

const buildStatusTimelineUsage = (draft: StatusTimelineDraft): ActivityTimelineBlock['usage'] | undefined =>
    !is.null(draft.firstTimestamp) && !is.null(draft.latestTimestamp)
        ? { durationMs: Math.max(0, draft.latestTimestamp - draft.firstTimestamp) }
        : undefined;

const buildStatusTimelineBlock = (draft: StatusTimelineDraft, isClosed: boolean): FlattenedBlock => {
    const block: ActivityTimelineBlock = {
        blockId: draft.blockId,
        steps: isClosed ? completeAllSteps(draft.stepsByKey) : normalizeStatusTimelineSteps(draft.stepsByKey),
        title: 'Agent activity',
        type: 'activity-timeline',
        usage: buildStatusTimelineUsage(draft),
    };
    return {
        block: isClosed ? { ...block, isClosed: true } : block,
        eventId: draft.eventId,
    };
};

// Any visible interruption (a question, a proposal, text) closes the running timeline; the steps
// after it open a fresh card below the interrupting content instead of accumulating off-screen
// in the card above it. Blocks that render nothing in the thread (e.g. a question asked in the
// composer) must not split — the run keeps accumulating into the same card.
export const compactStatusUpdates = (
    flat: readonly FlattenedBlock[],
    interruptsTimeline: (block: ChatBlock) => boolean = () => true,
): readonly FlattenedBlock[] => {
    const compacted: FlattenedBlock[] = [];
    let draft: StatusTimelineDraft | null = null;

    for (const item of flat) {
        if (item.block.type !== 'status-update') {
            if (!is.null(draft) && interruptsTimeline(item.block)) {
                compacted[draft.index] = buildStatusTimelineBlock(draft, true);
                draft = null;
            }
            compacted.push(item);
            continue;
        }

        if (is.null(draft)) {
            draft = createStatusTimelineDraft(compacted.length, item);
            compacted.push(item);
        }

        addStatusTimelineStep(draft, item);
    }

    if (is.null(draft)) {
        return compacted;
    }

    compacted[draft.index] = buildStatusTimelineBlock(draft, false);

    return compacted;
};
